// Состояния экрана: загрузка / ошибка («не ответили») / ответ. Ошибку никогда не рисуем как «пусто».
// Вид ошибки (errorKind) — по ApiError: нет сети → «Нет интернета» + «Повторить»; токен приложения не принят
// (invalid_access_token) → «Приложение недоступно» + «Повторить» (без автоповторов); 429 → текст с паузой сервера
// (Retry-After), кнопка доступна после паузы; прочее (5xx, сервер недоступен) → «Что-то пошло не так» + «Перезагрузить».
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, isAppTokenRejected } from '../api/client';
import { t } from '../i18n/i18n';
import emptyArt from '../assets/states/empty.png';
import errorArt from '../assets/states/error.png';
import offlineArt from '../assets/states/offline.png';
import infoArt from '../assets/states/info.svg';
import styles from './States.module.css';

/** Серверное пустое состояние (empty_state ответа экрана). */
export interface EmptyState {
  code: string;
  reason?: string;
  illustration?: string;
  title: string;
  text?: string;
  action?: { kind: string; label: string } | null;
}

export type ErrorKind = 'offline' | 'unavailable' | 'busy' | 'server';

export function errorKind(error: unknown): ErrorKind {
  if (!(error instanceof ApiError)) return 'server';
  if (error.kind === 'network') return 'offline';
  if (isAppTokenRejected(error)) return 'unavailable';
  if (error.status === 429) return 'busy';
  return 'server';
}

/** Пауза 429 в секундах: Retry-After / retry_after_seconds сервера; нет — 5 с. */
export const busySeconds = (error: unknown) => (error instanceof ApiError && error.retryAfterSeconds ? error.retryAfterSeconds : 5);

/** Иллюстрация, заголовок и подпись по центру экрана — так в макетах нарисованы пустые экраны и ошибки. */
export function StateView({ art, title, text, children }: {
  art: 'empty' | 'error' | 'offline' | 'info'; title: string; text?: string | null; children?: ReactNode;
}) {
  const src = { empty: emptyArt, error: errorArt, offline: offlineArt, info: infoArt }[art];
  return (
    <div className={styles.center}>
      <img className={styles.art} src={src} alt="" width={128} height={128} />
      <div className={styles.title}>{title}</div>
      {text && <div className={styles.text}>{text}</div>}
      {children}
    </div>
  );
}

/** Запрос, который ещё грузится: первая попытка уже не удалась (сеть, 5xx, 429 — клиент повторяет с паузой сервера) или
 *  запрос стоит на паузе без сети (TanStack Query не шлёт его офлайн) — показываем ошибку с «Повторить», а не вечный
 *  скелетон. Сеть вернулась — запрос уходит сам. */
export interface PendingQuery { failureCount: number; failureReason: unknown; fetchStatus?: string; refetch: () => unknown }

/** Скелетон: силуэт карточек со строками вместо спиннера — экран не «прыгает», когда приходит ответ. */
export function Loading({ cards = 3, rows = 3, query }: { cards?: number; rows?: number; query?: PendingQuery }) {
  if (query?.fetchStatus === 'paused') return <ErrorView error={new ApiError('network', 0, null)} onRetry={() => void query.refetch()} />;
  if (query && query.failureCount > 0) return <ErrorView error={query.failureReason} onRetry={() => void query.refetch()} />;
  return (
    <div className={styles.skeleton} role="status" aria-live="polite" aria-label={t('shell.loading')}>
      {Array.from({ length: cards }, (_, c) => (
        <div key={c} className={styles.skCard} aria-hidden="true">
          <span className={styles.skTitle} />
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} className={styles.skRow}>
              <span className={styles.skDot} />
              <span className={styles.skLine} style={{ width: `${70 - ((r + c) % 3) * 15}%` }} />
              <span className={styles.skTail} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Секунды до конца паузы 429 (0 — пауза прошла); отсчёт с момента, когда пришла эта ошибка. */
function useCountdown(error: unknown, active: boolean): number {
  const until = useMemo(() => (active ? Date.now() + busySeconds(error) * 1000 : 0), [error, active]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => { const n = Date.now(); setNow(n); if (n >= until) clearInterval(timer); }, 250);
    return () => clearInterval(timer);
  }, [until, active]);
  return active ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
}

/** Экран ошибки по виду: один запрос на одно нажатие (кнопка не повторяет сама). */
export function ErrorView({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const kind = errorKind(error);
  const wait = useCountdown(error, kind === 'busy');
  if (kind === 'offline') {
    return (
      <StateView art="offline" title={t('shell.offline.title')} text={t('shell.offline.text')}>
        <button className={styles.button} onClick={onRetry}>{t('shell.retry')}</button>
      </StateView>
    );
  }
  if (kind === 'unavailable') {
    return (
      <StateView art="error" title={t('shell.unavailable.title')} text={t('shell.unavailable.text')}>
        <button className={styles.button} onClick={onRetry}>{t('shell.retry')}</button>
      </StateView>
    );
  }
  return (
    <StateView art="error" title={t('shell.error.title')}
      text={kind === 'busy' && wait > 0 ? t('shell.busy.text', wait) : t('shell.error.text')}>
      <button className={styles.button} onClick={onRetry} disabled={kind === 'busy' && wait > 0}>{t('shell.error.reload')}</button>
    </StateView>
  );
}

/** Серверный empty_state: рисуется ВМЕСТО экрана. Кнопка — только для поддержанных действий. */
export function EmptyStateView({ state, onReload }: { state: EmptyState; onReload: () => void }) {
  const action = state.action?.kind === 'reload' ? state.action : null;
  return (
    <StateView art="empty" title={state.title} text={state.text}>
      {action && <button className={styles.button} onClick={onReload}>{action.label}</button>}
    </StateView>
  );
}
